import type { SimulacaoPrice } from '@/domain/simulador/factories/concrete/horizontal/price/simulacao-price'
import type { SimulacaoSacocH } from '@/domain/simulador/factories/concrete/horizontal/sacoch/simulacao-sacoch'
import type { SimulacaoSacocVParcelado } from '@/domain/simulador/factories/concrete/horizontal/sacocv/simulacao-sacocv-parcelado'
import type { SimulacaoSacocVSaldo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/simulacao-sacocv-saldo'
import type { SimulacaoVertical } from '@/domain/simulador/factories/concrete/vertical/simulacao-vertical'
import type {
  TParametrosDinamicosSimulacaoSimulador,
  TSimulacaoInput,
} from '@/features/simulador/brasil/types/simulacao.type'
import { type TTipoProdutoPrecificadoBrasilEnum } from '@/types/produto'
import type { TFormaDePagamentoEnum, TGetSimulacaoDuplicarResponse, TTipoAmortizacaoEnum } from '@/types/simulacao'

import { SimulacaoFactory } from './factories/simulacao-factory'

export class Empreendimento {
  private simulacoes: Array<
    SimulacaoVertical | SimulacaoSacocVParcelado | SimulacaoSacocVSaldo | SimulacaoPrice | SimulacaoSacocH
  > = []

  constructor(
    public readonly idExterno: string,
    public readonly nome: string,
    public readonly tipoDoProduto: TTipoProdutoPrecificadoBrasilEnum,
    public readonly tipoDeAmortizacao?: TTipoAmortizacaoEnum | null,
  ) {}

  criar(parametros: TParametrosDinamicosSimulacaoSimulador, formaPagamento?: TFormaDePagamentoEnum) {
    const primeiraSimulacao = this.adicionarSimulacao(parametros, formaPagamento)

    return {
      idExterno: this.idExterno,
      nome: this.nome,
      simulacoes: [primeiraSimulacao],
    }
  }

  adicionarSimulacao(
    parametros: TParametrosDinamicosSimulacaoSimulador,
    formaPagamento?: TFormaDePagamentoEnum,
  ): TSimulacaoInput {
    const simulacao = SimulacaoFactory.create(this.tipoDeAmortizacao, formaPagamento)
    this.simulacoes.push(simulacao)

    return {
      tipoDeAmortizacao: this.tipoDeAmortizacao,
      simulacao: simulacao.criar(parametros),
    } as TSimulacaoInput
  }

  duplicarSimulacao(
    simulacaoOriginal: TGetSimulacaoDuplicarResponse,
    parametros: TParametrosDinamicosSimulacaoSimulador,
  ): TSimulacaoInput {
    const novaSimulacao = SimulacaoFactory.create(
      simulacaoOriginal.tipoDeAmortizacao,
      simulacaoOriginal.formaDePagamento,
    )
    this.simulacoes.push(novaSimulacao)

    return {
      tipoDeAmortizacao: simulacaoOriginal.tipoDeAmortizacao,
      simulacao: novaSimulacao.duplicar(simulacaoOriginal, parametros),
    } as TSimulacaoInput
  }

  getSimulacao(
    index: number,
  ): SimulacaoVertical | SimulacaoSacocVParcelado | SimulacaoSacocVSaldo | SimulacaoPrice | SimulacaoSacocH | null {
    return this.simulacoes[index] ?? null
  }

  addSimulacaoExistente(
    simulacao: SimulacaoVertical | SimulacaoSacocVParcelado | SimulacaoSacocVSaldo | SimulacaoPrice | SimulacaoSacocH,
  ): void {
    this.simulacoes.push(simulacao)
  }

  substituirSimulacao(
    index: number,
    parametros: TParametrosDinamicosSimulacaoSimulador,
    novaFormaPagamento: TFormaDePagamentoEnum | null,
    tipoDeAmortizacao: TTipoAmortizacaoEnum | null | undefined = this.tipoDeAmortizacao,
  ): TSimulacaoInput {
    if (index < 0 || index >= this.simulacoes.length) {
      throw new Error(`Índice ${index} inválido para substituir simulação`)
    }

    const novaSimulacao = SimulacaoFactory.create(tipoDeAmortizacao ?? this.tipoDeAmortizacao, novaFormaPagamento)
    this.simulacoes[index] = novaSimulacao

    return {
      tipoDeAmortizacao: tipoDeAmortizacao ?? this.tipoDeAmortizacao,
      simulacao: novaSimulacao.criar(parametros),
    } as TSimulacaoInput
  }
}
